/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
const Backbone = require('lib/backbone')
const AppRouter = require('marionette.approuter').default
const _ = require('lib/underscore')
const log = require('log').create('router')

// copied from backbone.js - needed for some method overrides below
// Cached regular expressions for matching named param parts and splatted
// parts of route strings.
const optionalParam = /\((.*?)\)/g
const namedParam = /(\(\?)?:\w+/g
const splatParam = /\*\w+/g
const escapeRegExp = /[-{}[]+?.,\\\^$|#\s]/g

// override Backbone.History.prototype.route
// to allow for handlers to have a router associated with them
// this allows us to remove routes dynamically e.g. in router.destroy (see below)
Backbone.History.prototype.route = function addRoute(route, callback, router) {
    this.handlers.unshift({ router, route, callback })
}

const Router = AppRouter.extend({
    // need to audit this when upgrading Marionette `upgrade:marionette:audit`

    constructor: function constructor(options) {
        AppRouter.apply(this, arguments)

        // after router is initialized, check the state of Backbone.history
        _.bindAll(this, 'checkState')
        _.defer(this.checkState)
    },

    checkState() {
        if (Backbone.History.started) {
            // if this router was initialized after history started,
            // it might have added a route that needs to be triggered
            this.checkMyRoutes()
        } else {
            // console.log('Backbone.history not started yet');
            if (!Backbone.history) Backbone.history = new Backbone.History()
            Backbone.history.start({ pushState: true })
        }
    },

    getMyRoutes() {
        // returns the list of routes that belong to this router
        const routes = _.filter(Backbone.history.handlers, (handler) => {
            return handler.router === this
        })
        return routes
    },

    checkMyRoutes() {
        // check to see if any of my routes match the current fragment
        // this allows the router to bootstrap itself after history has started
        const fragment = Backbone.history.fragment
        const routes = this.getMyRoutes()

        return _.any(routes, (handler) => {
            if (handler.route.test(fragment)) {
                handler.callback(fragment)
                return true
            }
        })
    },

    // override Backbone.Router.route method
    // the only difference is that we pass a reference to `this` to Backbone.history.route
    // so this router's routes can be removed onDestroy
    // need to audit this when upgrading Backbone `upgrade:backbone:audit`
    route(route, name, callback) {
        if (!_.isRegExp(route)) route = this._routeToRegExp(route)
        if (_.isFunction(name)) {
            callback = name
            name = ''
        }
        if (!callback) callback = this[name]
        const router = this
        Backbone.history.route(
            route,
            function routeHandler(fragment) {
                const args = router._extractParameters(route, fragment)
                router.execute(callback, args)
                router.trigger.apply(router, [`route:${name}`].concat(args))
                router.trigger('route', name, args)
                Backbone.history.trigger('route', router, name, args)
            },
            this,
        )
        return this
    },

    // override Backbone.Router.prototype._routeToRegExp
    // just to make route regexes case-insensitive
    // Convert a route string into a regular expression, suitable for matching
    // against the current location hash.
    _routeToRegExp(route) {
        route = route
            .replace(escapeRegExp, '\\$&')
            .replace(optionalParam, '(?:$1)?')
            .replace(namedParam, (match, optional) => {
                return optional ? match : '([^/?]+)'
            })
            .replace(splatParam, '([^?]*?)')
        return new RegExp(`^${route}(?:\\?([\\s\\S]*))?$`, 'i')
    },

    // override AppRouter.prototype._addAppRoute
    // to prepend `scope` to all routes
    // need to audit this when upgrading Marionette `upgrade:marionette:audit`
    _addAppRoute(controller, route, methodName) {
        if (this.options.scope) {
            route = `${this.options.scope}(/)${route}`
        }
        AppRouter.prototype._addAppRoute.apply(this, [controller, route, methodName])
    },

    // override AppRouter.prototype.navigate
    // to prevent out-of-scope URL changes, and other automated niceties
    // need to audit this when upgrading Marionette `upgrade:marionette:audit`
    navigate(fragment, options) {
        options = options || {}

        const current_fragment = Backbone.history.fragment
        const scope = this.options.scope
        const defaults = {}
        let re

        if (scope) {
            if (fragment) {
                // prepend scope to fragment
                fragment = `${scope}/${fragment}`

                // if current URL ends in scope, this is additive, so set replace: true
                // we only do this if options.replace isn't already set to false (using _.defaults below)
                re = new RegExp(`${scope}/?$`, 'i')
                if (re.test(current_fragment)) {
                    defaults.replace = true
                }
            } else {
                // fragment is empty, use scope as fragment
                // this allows you to pass an empty string to reset URL to scope
                fragment = scope
            }

            // validate that requested URL is in scope
            // use `force: true` to override this scope check
            re = new RegExp(scope, 'i')
            if (!re.test(current_fragment)) {
                if (!options.force) {
                    log.error('Tab URL is out of scope.', scope, current_fragment)
                    return this
                }
            }
        }

        // if URL already exists in fragment, don't overwrite URL
        // use `force: true` to force overwrite
        re = new RegExp(fragment, 'i')
        if (current_fragment.match(re)) {
            // log('URL is already in place, no need to write');
            if (!options.force) return this
        }

        // if URL is the same, but case is different, just replace the history state
        // e.g. navigate to /mixedcase/collections, URL will be updated to /MixedCase/collections
        // but it won't add an additional state to the history, so back button still works
        re = new RegExp(`${fragment}/?$`, 'i')
        if (current_fragment.match(re)) {
            defaults.replace = true
        }

        // apply our defaults to the passed options
        // e.g. if replace is explicitly set to false, then we can't override it
        _.defaults(options, defaults)

        return AppRouter.prototype.navigate.apply(this, [fragment, options])
    },

    // custom destroy method
    // removes any handlers that belong to this router
    destroy() {
        log('destroy router for scope', this.options.scope)
        // log('Backbone.history.handlers.length', Backbone.history.handlers.length);
        Backbone.history.handlers = _.filter(Backbone.history.handlers, (handler) => {
            return handler.router !== this
        })
        // log('Backbone.history.handlers.length', Backbone.history.handlers.length);
    },
})

module.exports = Router
